import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard, Users, Video, HardDrive, LogOut, Menu, X, Shield,
  RefreshCw, Lock, Plus, Edit2, Trash2, CheckCircle, XCircle,
  Search, Download, ChevronLeft, ChevronRight, Eye, EyeOff, Upload,
  AlertTriangle, Unlock, BarChart3, FileText, CloudDownload, Play
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_API = `${BACKEND_URL}/api/admin-x7k9m2p4q8r1`;

const AdminPanel = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [inputPassword, setInputPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);

  // Dashboard state
  const [analytics, setAnalytics] = useState(null);

  // Users state
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(0);
  const [usersSearch, setUsersSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [editUser, setEditUser] = useState(null);

  // Modules state
  const [modules, setModules] = useState([]);
  const [editModule, setEditModule] = useState(null);

  // Vimeo state
  const [vimeoVideos, setVimeoVideos] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // Confirm delete state
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [deleteType, setDeleteType] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  // Access toggle confirmation state
  const [showAccessConfirm, setShowAccessConfirm] = useState(false);
  const [accessConfirmPassword, setAccessConfirmPassword] = useState('');
  const [accessToggleUser, setAccessToggleUser] = useState(null);
  const [accessToggleError, setAccessToggleError] = useState('');

  // Check stored password on mount
  useEffect(() => {
    const storedPassword = localStorage.getItem('admin_password');
    if (storedPassword) {
      setPassword(storedPassword);
      setIsAuthenticated(true);
    }
  }, []);

  // Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');
    try {
      await axios.post(`${ADMIN_API}/verify`, { password: inputPassword });
      localStorage.setItem('admin_password', inputPassword);
      setPassword(inputPassword);
      setIsAuthenticated(true);
    } catch (err) {
      setLoginError('Invalid admin password');
    } finally {
      setLoading(false);
    }
  };

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem('admin_password');
    setPassword('');
    setIsAuthenticated(false);
  };

  // Fetch dashboard analytics
  const fetchAnalytics = useCallback(async () => {
    if (!password) return;
    try {
      const response = await axios.get(`${ADMIN_API}/analytics/dashboard?password=${password}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  }, [password]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    if (!password) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${ADMIN_API}/users?password=${password}&skip=${usersPage * 20}&limit=20${usersSearch ? `&search=${usersSearch}` : ''}`
      );
      setUsers(response.data.users);
      setUsersTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  }, [password, usersPage, usersSearch]);

  // Fetch modules
  const fetchModules = useCallback(async () => {
    if (!password) return;
    try {
      const response = await axios.get(`${ADMIN_API}/modules?password=${password}`);
      setModules(response.data);
    } catch (error) {
      console.error('Failed to fetch modules:', error);
    }
  }, [password]);

  // Fetch Vimeo videos
  const fetchVimeoVideos = useCallback(async () => {
    if (!password) return;
    setLoading(true);
    try {
      const response = await axios.get(`${ADMIN_API}/vimeo/videos?password=${password}`);
      setVimeoVideos(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch Vimeo videos:', error);
    } finally {
      setLoading(false);
    }
  }, [password]);

  // Sync Vimeo videos to modules
  const handleVimeoSync = async () => {
    setSyncing(true);
    setSyncStatus(null);
    try {
      const response = await axios.post(`${ADMIN_API}/vimeo/sync?password=${password}`);
      setSyncStatus({ type: 'success', message: response.data.message });
      fetchModules();
      fetchVimeoVideos();
    } catch (error) {
      setSyncStatus({ type: 'error', message: error.response?.data?.detail || 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  // Load data when tab changes
  useEffect(() => {
    if (!isAuthenticated) return;
    if (activeTab === 'dashboard') fetchAnalytics();
    if (activeTab === 'users') fetchUsers();
    if (activeTab === 'modules') { fetchModules(); fetchVimeoVideos(); }
  }, [activeTab, isAuthenticated, fetchAnalytics, fetchUsers, fetchModules, fetchVimeoVideos]);

  // User actions
  const handleToggleAccess = async (userId) => {
    // Find the user to show in confirmation
    const user = users.find(u => u.user_id === userId);
    setAccessToggleUser(user);
    setAccessConfirmPassword('');
    setAccessToggleError('');
    setShowAccessConfirm(true);
  };

  const confirmToggleAccess = async () => {
    if (accessConfirmPassword !== 'chatisgod-1990') {
      setAccessToggleError('كلمة المرور غير صحيحة');
      return;
    }
    
    try {
      await axios.post(`${ADMIN_API}/users/${accessToggleUser.user_id}/toggle-access?password=${password}`);
      setShowAccessConfirm(false);
      setAccessToggleUser(null);
      setAccessConfirmPassword('');
      fetchUsers();
    } catch (error) {
      console.error('Failed to toggle access:', error);
      setAccessToggleError('فشل في تغيير حالة المستخدم');
    }
  };

  const handleGrantAccess = async () => {
    try {
      await axios.post(`${ADMIN_API}/users/grant-access?password=${password}`, { user_ids: selectedUsers });
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      console.error('Failed to grant access:', error);
    }
  };

  const handleRevokeAccess = async () => {
    try {
      await axios.post(`${ADMIN_API}/users/revoke-access?password=${password}`, { user_ids: selectedUsers });
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      console.error('Failed to revoke access:', error);
    }
  };

  // Export/Import functions
  const handleExportAll = async () => {
    try {
      const response = await axios.get(`${ADMIN_API}/export/full?password=${password}`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `energy-sacred-union-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  const handleImportFile = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = JSON.parse(e.target.result);
        const merge = window.confirm('Merge with existing data? (Cancel to replace all data)');
        await axios.post(`${ADMIN_API}/import/full?password=${password}&merge=${merge}`, data);
        alert('Import completed successfully!');
        fetchUsers();
        fetchModules();
        fetchAnalytics();
      } catch (error) {
        console.error('Import failed:', error);
        alert('Import failed. Please check the file format.');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleUpdateUser = async () => {
    try {
      await axios.put(`${ADMIN_API}/users/${editUser.user_id}?password=${password}`, editUser);
      setEditUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Failed to update user:', error);
    }
  };

  const handleUpdateModule = async () => {
    try {
      await axios.put(`${ADMIN_API}/modules/${editModule.module_id}?password=${password}`, editModule);
      setEditModule(null);
      fetchModules();
    } catch (error) {
      console.error('Failed to update module:', error);
    }
  };

  const handleDelete = async () => {
    try {
      if (deleteType === 'users') {
        await axios.post(
          `${ADMIN_API}/users/bulk-delete?password=${password}&confirm_password=${confirmPassword}`,
          { user_ids: selectedUsers }
        );
        setSelectedUsers([]);
        fetchUsers();
      } else if (deleteType === 'module') {
        await axios.delete(`${ADMIN_API}/modules/${deleteId}?password=${password}&confirm_password=${confirmPassword}`);
        fetchModules();
      }
      setShowConfirm(false);
      setConfirmPassword('');
      setDeleteId(null);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handleExportUsers = async () => {
    try {
      const response = await axios.get(`${ADMIN_API}/users/export/csv?password=${password}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'users_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to export:', error);
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0f1117] flex items-center justify-center p-4">
        <div className="bg-[#1a1f2e] rounded-2xl p-8 w-full max-w-md border border-gray-700">
          <div className="flex items-center justify-center mb-8">
            <div className="w-16 h-16 bg-[#d4a33a]/20 rounded-full flex items-center justify-center">
              <Shield className="w-8 h-8 text-[#d4a33a]" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white text-center mb-2">Admin Panel</h1>
          <p className="text-gray-400 text-center mb-8">Enter password to access</p>
          
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={inputPassword}
              onChange={(e) => setInputPassword(e.target.value)}
              placeholder="Admin Password"
              className="w-full bg-[#232836] border border-gray-600 rounded-lg px-4 py-3 text-white mb-4 focus:outline-none focus:border-[#d4a33a]"
            />
            {loginError && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500 rounded-lg text-red-400 text-sm">{loginError}</div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#d4a33a] hover:bg-[#c49530] text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <><Lock className="w-5 h-5" /> Access Dashboard</>}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'modules', label: 'Modules', icon: Video },
  ];

  return (
    <div className="min-h-screen bg-[#0f1117] flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-[#1a1f2e] border-r border-gray-700 transition-all duration-300 flex flex-col fixed h-screen z-40`}>
        <div className="p-4 border-b border-gray-700 flex items-center gap-3">
          <div className="w-10 h-10 bg-[#d4a33a] rounded-lg flex items-center justify-center flex-shrink-0">
            <Shield className="w-6 h-6 text-white" />
          </div>
          {sidebarOpen && <h1 className="text-white font-bold">Admin Panel</h1>}
        </div>

        <nav className="flex-1 p-4 space-y-2">
          {tabs.map((tab) => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === tab.id ? 'bg-[#d4a33a]/20 text-[#d4a33a]' : 'text-gray-400 hover:bg-[#232836]'
                }`}
              >
                <TabIcon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span>{tab.label}</span>}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-gray-700 space-y-2">
          <button onClick={() => navigate('/')} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-[#232836]">
            <X className="w-5 h-5" /> {sidebarOpen && 'Back to Site'}
          </button>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10">
            <LogOut className="w-5 h-5" /> {sidebarOpen && 'Logout'}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-20'} transition-all duration-300`}>
        <header className="bg-[#1a1f2e] border-b border-gray-700 px-4 py-4 flex items-center justify-between sticky top-0 z-30">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-[#232836] rounded-lg text-gray-400">
            <Menu className="w-6 h-6" />
          </button>
        </header>

        <div className="p-6">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Dashboard Overview</h2>
                <div className="flex gap-3">
                  <button
                    onClick={handleExportAll}
                    className="flex items-center gap-2 px-4 py-2 bg-[#232836] hover:bg-[#2a3142] text-white rounded-lg border border-gray-700"
                  >
                    <Download className="w-4 h-4" />
                    Export All Data
                  </button>
                  <label className="flex items-center gap-2 px-4 py-2 bg-[#d4a33a] hover:bg-[#c49530] text-white rounded-lg cursor-pointer">
                    <Upload className="w-4 h-4" />
                    Import Data
                    <input type="file" accept=".json" onChange={handleImportFile} className="hidden" />
                  </label>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-[#232836] rounded-xl p-6 border border-gray-700">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Total Users</p>
                      <p className="text-2xl font-bold text-white">{analytics?.users?.total || 0}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#232836] rounded-xl p-6 border border-gray-700">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Paid Users</p>
                      <p className="text-2xl font-bold text-white">{analytics?.users?.with_access || 0}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#232836] rounded-xl p-6 border border-gray-700">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                      <Plus className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Today Signups</p>
                      <p className="text-2xl font-bold text-white">{analytics?.users?.today_signups || 0}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#232836] rounded-xl p-6 border border-gray-700">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-cyan-500 rounded-lg flex items-center justify-center">
                      <Eye className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Video Views</p>
                      <p className="text-2xl font-bold text-white">{analytics?.videos?.total_views || 0}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">User Management</h2>
                <button onClick={handleExportUsers} className="flex items-center gap-2 px-4 py-2 bg-[#232836] text-white rounded-lg hover:bg-[#2a3142]">
                  <Download className="w-4 h-4" /> Export
                </button>
              </div>

              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by email or name..."
                  value={usersSearch}
                  onChange={(e) => { setUsersSearch(e.target.value); setUsersPage(0); }}
                  className="w-full bg-[#232836] border border-gray-600 rounded-lg pl-10 pr-4 py-3 text-white focus:outline-none focus:border-[#d4a33a]"
                />
              </div>

              {selectedUsers.length > 0 && (
                <div className="mb-4 p-4 bg-[#232836] rounded-lg flex flex-wrap items-center gap-3">
                  <span className="text-white">{selectedUsers.length} selected</span>
                  <button onClick={handleGrantAccess} className="flex items-center gap-1 px-3 py-1.5 bg-green-500/20 text-green-400 rounded hover:bg-green-500/30">
                    <Unlock className="w-4 h-4" /> Grant Access
                  </button>
                  <button onClick={handleRevokeAccess} className="flex items-center gap-1 px-3 py-1.5 bg-amber-500/20 text-amber-400 rounded hover:bg-amber-500/30">
                    <Lock className="w-4 h-4" /> Revoke Access
                  </button>
                  <button onClick={() => { setDeleteType('users'); setShowConfirm(true); }} className="flex items-center gap-1 px-3 py-1.5 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30">
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </div>
              )}

              <div className="bg-[#232836] rounded-xl border border-gray-700 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#1a1f2e]">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input 
                          type="checkbox" 
                          checked={selectedUsers.length === users.length && users.length > 0} 
                          onChange={() => setSelectedUsers(selectedUsers.length === users.length ? [] : users.map(u => u.user_id))} 
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-gray-400 font-medium">User ID</th>
                      <th className="px-4 py-3 text-left text-gray-400 font-medium">Name</th>
                      <th className="px-4 py-3 text-left text-gray-400 font-medium">Email</th>
                      <th className="px-4 py-3 text-left text-gray-400 font-medium">Course Access</th>
                      <th className="px-4 py-3 text-left text-gray-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-400"><RefreshCw className="w-6 h-6 animate-spin mx-auto" /></td></tr>
                    ) : users.length === 0 ? (
                      <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-400">No users found</td></tr>
                    ) : (
                      users.map((user) => (
                        <tr key={user.user_id} className="border-t border-gray-700 hover:bg-[#2a3142]">
                          <td className="px-4 py-3">
                            <input 
                              type="checkbox" 
                              checked={selectedUsers.includes(user.user_id)} 
                              onChange={() => setSelectedUsers(prev => prev.includes(user.user_id) ? prev.filter(id => id !== user.user_id) : [...prev, user.user_id])} 
                            />
                          </td>
                          <td className="px-4 py-3 text-gray-300 font-mono text-sm">{user.user_id}</td>
                          <td className="px-4 py-3 text-white">{user.name}</td>
                          <td className="px-4 py-3 text-gray-300">{user.email}</td>
                          <td className="px-4 py-3">
                            <button 
                              onClick={() => handleToggleAccess(user.user_id)}
                              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                user.has_access 
                                  ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30' 
                                  : 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30'
                              }`}
                              title={user.has_access ? 'Click to revoke access' : 'Click to grant access'}
                            >
                              {user.has_access ? (
                                <>
                                  <Unlock className="w-4 h-4" />
                                  Active
                                </>
                              ) : (
                                <>
                                  <Lock className="w-4 h-4" />
                                  Blocked
                                </>
                              )}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <button onClick={() => setEditUser(user)} className="p-2 hover:bg-[#3a4152] rounded text-gray-400 hover:text-white">
                              <Edit2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
                  <span className="text-gray-400 text-sm">Total: {usersTotal}</span>
                  <div className="flex gap-2">
                    <button onClick={() => setUsersPage(p => Math.max(0, p - 1))} disabled={usersPage === 0} className="p-2 bg-[#1a1f2e] rounded disabled:opacity-50">
                      <ChevronLeft className="w-5 h-5 text-gray-400" />
                    </button>
                    <button onClick={() => setUsersPage(p => p + 1)} disabled={(usersPage + 1) * 20 >= usersTotal} className="p-2 bg-[#1a1f2e] rounded disabled:opacity-50">
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Modules Tab */}
          {activeTab === 'modules' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Course Modules</h2>
                <button 
                  onClick={handleVimeoSync}
                  disabled={syncing}
                  className="flex items-center gap-2 px-4 py-2 bg-[#d4a33a] hover:bg-[#c49530] text-white rounded-lg font-medium disabled:opacity-50"
                >
                  {syncing ? <RefreshCw className="w-5 h-5 animate-spin" /> : <CloudDownload className="w-5 h-5" />}
                  {syncing ? 'Syncing...' : 'Sync from Vimeo'}
                </button>
              </div>

              {/* Sync Status Message */}
              {syncStatus && (
                <div className={`mb-4 p-4 rounded-lg flex items-center gap-3 ${
                  syncStatus.type === 'success' ? 'bg-green-500/10 border border-green-500 text-green-400' : 'bg-red-500/10 border border-red-500 text-red-400'
                }`}>
                  {syncStatus.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                  {syncStatus.message}
                </div>
              )}

              {/* Vimeo Videos Preview */}
              {vimeoVideos.length > 0 && (
                <div className="mb-6 bg-[#1a1f2e] rounded-xl p-4 border border-gray-700">
                  <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                    <Play className="w-5 h-5 text-[#d4a33a]" />
                    Vimeo Folder: كورس علاج الطاقتين ايميرجين ({vimeoVideos.length} videos)
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {vimeoVideos.map((video) => (
                      <div key={video.vimeo_id} className="bg-[#232836] rounded-lg p-3 flex items-center gap-3">
                        {video.thumbnail ? (
                          <img src={video.thumbnail} alt={video.name} className="w-16 h-10 object-cover rounded" />
                        ) : (
                          <div className="w-16 h-10 bg-gray-700 rounded flex items-center justify-center">
                            <Video className="w-5 h-5 text-gray-500" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium truncate">{video.name}</p>
                          <p className="text-gray-400 text-xs">{Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-gray-400 text-sm mt-3">
                    Click "Sync from Vimeo" to import these videos as course modules. This will replace existing modules.
                  </p>
                </div>
              )}

              {/* Empty Folder Notice */}
              {vimeoVideos.length === 0 && !loading && (
                <div className="mb-6 bg-amber-500/10 border border-amber-500 rounded-xl p-4">
                  <p className="text-amber-400">
                    <strong>No videos in Vimeo folder.</strong> Add videos to your "كورس علاج الطاقتين ايميرجين" folder on Vimeo, then click "Sync from Vimeo".
                  </p>
                </div>
              )}

              {/* Current Modules */}
              {modules.length === 0 ? (
                <div className="bg-[#232836] rounded-xl p-12 text-center border border-gray-700">
                  <Video className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">No Modules Yet</h3>
                  <p className="text-gray-400 mb-4">Upload videos to Vimeo, then click "Sync from Vimeo" to import them as course modules.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {modules.map((module) => (
                    <div key={module.module_id} className="bg-[#232836] rounded-xl p-4 border border-gray-700 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {module.thumbnail ? (
                          <img src={module.thumbnail} alt={module.title} className="w-16 h-10 object-cover rounded-lg" />
                        ) : (
                          <div className="w-10 h-10 bg-[#d4a33a] rounded-lg flex items-center justify-center">
                            <Video className="w-5 h-5 text-white" />
                          </div>
                        )}
                        <div>
                          <p className="text-white font-medium">Part {module.part} - Module {module.order}: {module.title}</p>
                          <p className="text-gray-400 text-sm">
                            {module.vimeo_video_id ? `Vimeo ID: ${module.vimeo_video_id}` : module.module_id}
                            {module.duration_seconds ? ` • ${Math.floor(module.duration_seconds / 60)}:${(module.duration_seconds % 60).toString().padStart(2, '0')}` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setEditModule(module)} className="p-2 hover:bg-[#3a4152] rounded text-gray-400 hover:text-white">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => { setDeleteType('module'); setDeleteId(module.module_id); setShowConfirm(true); }} className="p-2 hover:bg-red-500/20 rounded text-gray-400 hover:text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Edit User Modal */}
      {editUser && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#1a1f2e] rounded-xl p-6 w-full max-w-md border border-gray-700">
            <h3 className="text-xl font-bold text-white mb-4">Edit User</h3>
            <div className="space-y-4">
              <input type="email" value={editUser.email} onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                className="w-full bg-[#232836] border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none" />
              <input type="text" value={editUser.name} onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                className="w-full bg-[#232836] border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none" />
              <label className="flex items-center gap-2 text-white">
                <input type="checkbox" checked={editUser.has_access} onChange={(e) => setEditUser({ ...editUser, has_access: e.target.checked })} />
                Course access
              </label>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setEditUser(null)} className="flex-1 bg-gray-600 text-white py-2.5 rounded-lg">Cancel</button>
              <button onClick={handleUpdateUser} className="flex-1 bg-[#d4a33a] text-white py-2.5 rounded-lg">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Module Modal */}
      {editModule && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#1a1f2e] rounded-xl p-6 w-full max-w-md border border-gray-700">
            <h3 className="text-xl font-bold text-white mb-4">Edit Module</h3>
            <div className="space-y-4">
              <input type="text" placeholder="Title" value={editModule.title} onChange={(e) => setEditModule({ ...editModule, title: e.target.value })}
                className="w-full bg-[#232836] border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none" />
              <input type="text" placeholder="Vimeo Video ID" value={editModule.vimeo_video_id || ''} onChange={(e) => setEditModule({ ...editModule, vimeo_video_id: e.target.value })}
                className="w-full bg-[#232836] border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none" />
              <label className="flex items-center gap-2 text-white">
                <input type="checkbox" checked={editModule.is_published} onChange={(e) => setEditModule({ ...editModule, is_published: e.target.checked })} />
                Published
              </label>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setEditModule(null)} className="flex-1 bg-gray-600 text-white py-2.5 rounded-lg">Cancel</button>
              <button onClick={handleUpdateModule} className="flex-1 bg-[#d4a33a] text-white py-2.5 rounded-lg">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#1a1f2e] rounded-xl p-6 w-full max-w-md border border-gray-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Confirm Delete</h3>
                <p className="text-gray-400 text-sm">This action cannot be undone</p>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-gray-400 text-sm mb-2">Re-enter admin password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-[#232836] border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none"
              />
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setShowConfirm(false); setConfirmPassword(''); }} className="flex-1 bg-gray-600 text-white py-2.5 rounded-lg">Cancel</button>
              <button onClick={handleDelete} className="flex-1 bg-red-500 text-white py-2.5 rounded-lg">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Access Toggle Confirmation Modal */}
      {showAccessConfirm && accessToggleUser && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#1a1f2e] rounded-xl p-6 w-full max-w-md border border-gray-700">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                accessToggleUser.has_access ? 'bg-red-500/20' : 'bg-green-500/20'
              }`}>
                {accessToggleUser.has_access ? (
                  <Lock className="w-6 h-6 text-red-500" />
                ) : (
                  <Unlock className="w-6 h-6 text-green-500" />
                )}
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">
                  {accessToggleUser.has_access ? 'إلغاء تفعيل المستخدم' : 'تفعيل المستخدم'}
                </h3>
                <p className="text-gray-400 text-sm">{accessToggleUser.name}</p>
              </div>
            </div>
            
            <div className="bg-[#232836] rounded-lg p-4 mb-4" dir="rtl">
              <p className="text-white text-center">
                {accessToggleUser.has_access 
                  ? 'هل أنت متأكد من إلغاء تفعيل هذا المستخدم؟'
                  : 'هل أنت متأكد من تفعيل هذا المستخدم؟'
                }
              </p>
              <p className="text-gray-400 text-sm text-center mt-2">
                {accessToggleUser.email}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-gray-400 text-sm mb-2">أدخل كلمة مرور الأدمن للتأكيد</label>
              <input
                type="password"
                value={accessConfirmPassword}
                onChange={(e) => { setAccessConfirmPassword(e.target.value); setAccessToggleError(''); }}
                placeholder="كلمة المرور"
                className="w-full bg-[#232836] border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#d4a33a]"
              />
              {accessToggleError && (
                <p className="text-red-400 text-sm mt-2">{accessToggleError}</p>
              )}
            </div>
            
            <div className="flex gap-3">
              <button 
                onClick={() => { setShowAccessConfirm(false); setAccessToggleUser(null); setAccessConfirmPassword(''); setAccessToggleError(''); }} 
                className="flex-1 bg-gray-600 hover:bg-gray-500 text-white py-2.5 rounded-lg transition-colors"
              >
                إلغاء
              </button>
              <button 
                onClick={confirmToggleAccess} 
                className={`flex-1 text-white py-2.5 rounded-lg transition-colors ${
                  accessToggleUser.has_access 
                    ? 'bg-red-500 hover:bg-red-600' 
                    : 'bg-green-500 hover:bg-green-600'
                }`}
              >
                {accessToggleUser.has_access ? 'إلغاء التفعيل' : 'تفعيل'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
